/**
 * 授权状态（前端侧）
 * ============================================================
 *
 * 这里**只做展示与引导**，绝不作为"能不能用"的判据 ——
 * 真正的判定在服务端。前端拿到的额度只用来显示"还剩多少"，
 * 改这里没有任何用（改了也发不出请求）。
 */

import api from './api';

const CACHE_KEY = 'dg:entitlement';

/** 上一次拿到的授权状态（用于首屏先显示，避免闪一下"未授权"） */
export function readCached() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    if (data) localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    else localStorage.removeItem(CACHE_KEY);
  } catch {
    /* 隐私模式下 localStorage 可能不可写，忽略 */
  }
}

/**
 * 启动时注册设备（幂等，重复调用不会重置试用期）
 *
 * 服务端没启用授权时返回 { enabled: false }，
 * 此时所有功能照常，界面也不显示任何额度信息。
 */
export async function register() {
  const resp = await api.post('/api/client/register', {});
  const data = resp.data?.data || null;
  writeCache(data);
  return data;
}

/** 查当前状态与今日剩余额度 */
export async function fetchQuota() {
  const resp = await api.get('/api/client/quota');
  const data = resp.data?.data || null;
  writeCache(data);
  return data;
}

/**
 * 激活卡密
 * @param {string} code
 * @returns {Promise<{ok:boolean, message:string, data?:Object}>}
 */
export async function redeem(code) {
  try {
    const resp = await api.post('/api/client/redeem', { code });
    const data = resp.data?.data || null;
    writeCache(data);
    return { ok: true, message: data?.message || '激活成功', data };
  } catch (e) {
    return {
      ok: false,
      message: e?.response?.data?.error || e.message || '激活失败',
      code: e?.response?.data?.code,
    };
  }
}

/**
 * 文案：把状态翻译成用户能懂的话
 *
 * 「试用还剩 9 天」比「trial_days_left: 9」有用得多。
 */
export function describeStatus(ent) {
  if (!ent || ent.enabled === false) return null;

  if (ent.status === 'licensed') {
    const days = ent.license?.expiresAt
      ? Math.max(
          0,
          Math.ceil((new Date(ent.license.expiresAt).getTime() - Date.now()) / 86400000)
        )
      : null;
    return {
      tone: 'ok',
      title: days != null ? `已激活 · 还剩 ${days} 天` : '已激活',
      detail: `今日剩余 ${ent.daily?.remaining ?? 0} / ${ent.daily?.limit ?? 0} 点`,
    };
  }

  if (ent.status === 'trial') {
    return {
      tone: 'trial',
      title: `试用中 · 还剩 ${ent.trialDaysLeft ?? 0} 天`,
      detail: `今日剩余 ${ent.daily?.remaining ?? 0} / ${ent.daily?.limit ?? 0} 点`,
    };
  }

  if (ent.status === 'expired') {
    return {
      tone: 'expired',
      title: '试用已结束',
      detail: '课程和已生成的片段仍可学习；AI 对话需要输入卡密',
    };
  }

  if (ent.status === 'blocked') {
    return { tone: 'expired', title: '设备已停用', detail: '请联系客服' };
  }

  return null;
}
